/**
 * Shared Markdown renderer — `punica.shell.Markdown`.
 *
 * The single, substrate-central markdown→HTML renderer consumed by every UI
 * surface (chat assistant answers, the .md file viewer, …) so code blocks and
 * typography look identical everywhere. Backed by markdown-it with
 * syntax-highlighted fenced code via highlight.js.
 *
 * D1 (substrate ships NO DOM): this module is pure data only — `render()`
 * returns an HTML *string* and `css` is a *string* constant. Interactive
 * chrome (copy buttons, etc.) is a DOM concern and lives in the consuming
 * extensions, layered over the rendered string.
 *
 * Safety: raw HTML in the source is escaped by default (prompt-injected
 * `<img onerror>` can never execute); markdown-it validates link schemes; and
 * highlight.js escapes the code it tokenizes. The `highlight` callback's
 * output is therefore trusted, already-escaped HTML.
 *
 * The `html: true` option opts out of that escaping for surfaces whose source
 * is the user's own document rather than model output — notebook markdown
 * cells, where `<div class="alert alert-info">` is the Jupyter convention. It
 * returns UNSANITIZED HTML and the caller must sanitize before inserting it
 * into the DOM, which is where Jupyter itself draws the line (render, then
 * DOMPurify). Keeping the sanitizer out of here is also what keeps D1 true:
 * this module never touches the DOM.
 */

import type { shell } from 'punica';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js/lib/core';

// Curated language set — keeps the bundle small (core + these, NOT the ~1MB
// full build). Covers what shows up in chat/docs; unknown languages fall back
// to escaped plain text.
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import json from 'highlight.js/lib/languages/json';
import yaml from 'highlight.js/lib/languages/yaml';
import bash from 'highlight.js/lib/languages/bash';
import xml from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import sql from 'highlight.js/lib/languages/sql';
import markdown from 'highlight.js/lib/languages/markdown';
import diff from 'highlight.js/lib/languages/diff';

import markdownCss from './markdown.css';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('json', json);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('css', css);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('diff', diff);

// Common aliases → registered languages.
const LANG_ALIASES: Record<string, string> = {
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  yml: 'yaml',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  html: 'xml',
  htm: 'xml',
  md: 'markdown'
};

function resolveLang(lang: string): string | undefined {
  const key = (lang || '').trim().toLowerCase();
  if (!key) return undefined;
  const resolved = LANG_ALIASES[key] ?? key;
  return hljs.getLanguage(resolved) ? resolved : undefined;
}

/**
 * One renderer per (highlight, html, breaks) triple. Streaming re-renders per
 * token, where highlighting would thrash on partial code — callers pass
 * `highlight: false` during a stream and `true` once the block is sealed.
 */
function createRenderer(
  highlight: boolean,
  html: boolean,
  breaks: boolean
): MarkdownIt {
  const md = new MarkdownIt({
    html, // raw HTML escaped unless the caller opts in (and sanitizes)
    linkify: true, // auto-link bare URLs
    breaks, // default true: single newline -> <br> (chat-friendly)
    highlight: highlight
      ? (code: string, lang: string): string => {
          const resolved = resolveLang(lang);
          if (resolved) {
            try {
              return hljs.highlight(code, {
                language: resolved,
                ignoreIllegals: true
              }).value;
            } catch {
              /* fall through to plain escape */
            }
          }
          // Unknown language → let markdown-it escape it (return "" so it uses
          // its default escaping).
          return '';
        }
      : undefined
  });
  return md;
}

/** Built on first use and kept — eight combinations exist, few are used. */
const renderers = new Map<string, MarkdownIt>();

function getRenderer(
  highlight: boolean,
  html: boolean,
  breaks: boolean
): MarkdownIt {
  const key = `${highlight}|${html}|${breaks}`;
  let md = renderers.get(key);
  if (!md) {
    md = createRenderer(highlight, html, breaks);
    renderers.set(key, md);
  }
  return md;
}

/**
 * This interface deliberately does NOT come from 'punica'. The contract the
 * consumers see lives in `@punica/editor`'s
 * `types/punica.module.shell.markdown.d.ts`, but a new option is born HERE
 * first and reaches the published contract types one @punica/editor release
 * later — typing the implementation off the published contract would make
 * adding an option uncompilable until the release that describes it. The
 * guard below closes the drift that matters in the other direction: every
 * option the published contract advertises must exist here, so the shell can
 * never silently ignore an option an extension was told it may pass.
 */
export interface RenderMarkdownOptions {
  /**
   * Syntax-highlight fenced code blocks (default true). Pass false for
   * per-token streaming — highlighting is applied once the block is finalized.
   */
  highlight?: boolean;
  /**
   * Render raw HTML found in the source instead of escaping it (default
   * false). The returned string is then UNSANITIZED — the caller must
   * sanitize it before inserting it into the DOM. Only surfaces whose source
   * is the user's own document should pass true; anything carrying model
   * output must not.
   */
  html?: boolean;
  /**
   * Treat a single newline as a hard line break, `<br>` (default true, the
   * chat-friendly reading). Notebook markdown cells pass false: Jupyter joins
   * soft-wrapped source lines into one paragraph, so a cell hard-wrapped at
   * 78 columns must not render as a column of short lines.
   */
  breaks?: boolean;
}

/**
 * Contract-drift guard, compile-time only. `Exclude` of the published
 * contract's keys from the implementation's keys must be `never`: an option
 * that `@punica/editor` advertises and this renderer does not know would be
 * silently dropped for every caller. The reverse direction is legal by
 * design — this implementation runs ahead of the published contract while a
 * new option ships (see the note on RenderMarkdownOptions).
 */
type OptionTheContractHasAndWeDropped = Exclude<
  keyof shell.Markdown.RenderOptions,
  keyof RenderMarkdownOptions
>;
type AssertNever<T extends never> = T;
export type _renderOptionsCoverContract =
  AssertNever<OptionTheContractHasAndWeDropped>;

/**
 * Render a Markdown string to an HTML fragment (DOM-free). Safe to insert
 * as-is unless `html: true` was passed — see `RenderMarkdownOptions.html`.
 */
export function renderMarkdown(
  src: string,
  opts?: RenderMarkdownOptions
): string {
  const md = getRenderer(
    opts?.highlight !== false,
    opts?.html === true,
    opts?.breaks !== false
  );
  return md.render(String(src ?? ''));
}

/**
 * Stylesheet for the rendered HTML — hljs One Dark token palette (matching the
 * editor's existing code coloring), fenced-code container + chrome hooks, and
 * modest markdown typography. Consumers inject this once via
 * `punica.extensions.Styles.register('punica:markdown', css, …)`.
 *
 * The `.pnc-md-code*` classes are chrome hooks the extensions add around each
 * `<pre>` (language label + copy button); the base rendering works without
 * them.
 */
export const MARKDOWN_CSS = markdownCss;

export default {
  render: renderMarkdown,
  css: MARKDOWN_CSS
};
